'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import type { SubContainerType } from '../sub-container-types.types';

interface SubContainerTypeCompactRowProps {
  item: SubContainerType;
  onEdit: (item: SubContainerType) => void;
  onDelete: (item: SubContainerType) => void;
}

export function SubContainerTypeCompactRow({
  item,
  onEdit,
  onDelete
}: SubContainerTypeCompactRowProps) {
  const t = useTranslations('containerTypes');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <span className='font-medium'>{item.name}</span>
      </div>

      <span className='text-muted-foreground hidden text-sm sm:inline'>
        {item.container_type?.name ?? '—'}
      </span>

      <Badge
        variant={item.status === 'active' ? 'default' : 'secondary'}
        className={cn(
          item.status === 'active'
            ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
            : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
        )}
      >
        {item.status === 'active'
          ? t('subStatusActive')
          : t('subStatusInactive')}
      </Badge>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['sub_container_types.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(item)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['sub_container_types.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onDelete(item)}
          >
            <Trash2 className='text-destructive h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
